import React from "react";
import { Button, Box } from "@mui/material";
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { createPortal } from "react-dom";
import SearchIcon from '@mui/icons-material/Search';

export function DesktopIndexMenu({
  groupedCategoriesLabels,
  groupedCategories,
  selectedItems,
  handleMenuItemClick,
  t,
  isArabic,
  getSelectedLabel,
  setCustomIndexDialogOpen,
}) {
  const [hoveredCategory, setHoveredCategory] = React.useState(null);
  const [hoveredType, setHoveredType] = React.useState(null);
  const [hoveredOptionIdx, setHoveredOptionIdx] = React.useState(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const menuButtonRef = React.useRef();
  const closeTimeoutRef = React.useRef();

  // Enhanced open menu with animation state
  const handleOpenMenu = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setMenuOpen(true);
    setIsAnimating(true);
    // Reset animation state after animation completes
    setTimeout(() => setIsAnimating(false), 150);
  };

  // Enhanced close with slight delay to prevent accidental closes
  const handleCloseMenu = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setMenuOpen(false);
      setHoveredCategory(null);
      setHoveredType(null);
      setHoveredOptionIdx(null);
    }, 100);
  };

  // Cancel close if mouse re-enters
  const handleMouseEnterMenu = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Enhanced keyboard navigation
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleCloseMenu();
    }
  };

  React.useEffect(() => {
    if (menuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [menuOpen]);

  return (
    <div
      style={{ display: "inline-block", position: "relative", zIndex: 30 }}
      onMouseLeave={handleCloseMenu}
      onMouseEnter={handleMouseEnterMenu}
    >
      {/* Main Button */}
      <Button
        ref={menuButtonRef}
        onMouseEnter={handleOpenMenu}
        onClick={handleOpenMenu}
        sx={{
          color: "black",
          border: "1px solid #339ace",
          borderRadius: "15px",
          padding: "8px 16px",
          backgroundColor: "white",
          fontWeight: "bold",
          minHeight: "42px",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: "#rgba(255, 255, 255, 0.09)",
            borderColor: "#2b8bb8",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(51, 154, 206, 0.15)",
          },
          "&:active": {
            transform: "translateY(0px)",
          },
        }}
      >
        <div 
          className="button-content" 
          style={{ 
            display: "flex", 
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div className="selected-category" style={{ color: "black" }}>
            {selectedItems.currentCategory
              ? groupedCategoriesLabels[selectedItems.currentCategory]?.["name"]
              : t("Map.index")}
          </div>
          <div style={{ color: "black", fontSize: "14px" }}>|</div>
          <div 
            className="selected-index" 
            style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "6px",
              color: "#054e73",
              fontWeight: "800",
            }}
          >
            {getSelectedLabel && getSelectedLabel()}
            <LayersOutlinedIcon 
              sx={{ 
                fontSize: "20px",
                transition: "transform 0.2s",
              }} 
            />
          </div>
        </div>
      </Button>

      {/* Enhanced Menu with animations */}
      {menuOpen && createPortal(
        <div
          style={{
            position: "absolute",
            left: isArabic ? 'unset' : 87,
            top: '3.4rem',
            right: isArabic ? 87 : 'unset',
            zIndex: 1400,
            background: "rgba(255, 255, 255, 0.9)",
            // backdropFilter: "blur(8px)",
            border: "1px solid rgba(51, 154, 206, 0.1)",
            borderRadius: "16px",
            width: 'max-content',
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
            display: "flex",
            overflow: "hidden",
            opacity: isAnimating ? 0 : 1,
            transform: isAnimating ? "translateY(-8px) scale(0.95)" : "translateY(0px) scale(1)",
            transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
          onMouseEnter={handleMouseEnterMenu}
        >
          {/* Enhanced First column: categories */}
          <div style={{ width: 'max-content', minWidth: "150px" }}>
            {Object.keys(groupedCategories).map((category, index) => (
              <div
                key={category}
                onMouseEnter={() => {
                  setHoveredCategory(category);
                  setHoveredType(null);
                  setHoveredOptionIdx(null);
                }}
                style={{
                  padding: "12px 20px",
                  background:
                    selectedItems.currentCategory === category
                      ? "linear-gradient(90deg, #339ace 0%, #2b8bb8 100%)"
                      : hoveredCategory === category
                      ? "linear-gradient(90deg,rgb(116, 196, 236) 0%,rgb(47, 160, 212) 100%)"
                      : undefined,
                  color:
                    selectedItems.currentCategory === category
                      ? "#fff"
                      : hoveredCategory === category
                      ? "#fff"
                      : "#444",
                  cursor: "pointer",
                  textAlign: isArabic ? "right" : "left",
                  fontWeight: selectedItems.currentCategory === category ? "800" : "700",
                  borderRadius: "0px",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  borderBottom: index === Object.keys(groupedCategories).length - 1 ? "none" : "1px solid rgba(0,0,0,0.05)",
                  position: "relative",
                  "&::after": hoveredCategory === category ? {
                    content: '""',
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "3px",
                    height: "60%",
                    backgroundColor: "#339ace",
                    borderRadius: "2px",
                  } : undefined,
                }}
              >
                {groupedCategoriesLabels[category]?.["name"]}
                {hoveredCategory === category && (
                  <div
                    style={{
                      position: "absolute",
                      right: isArabic ? "unset" : "8px",
                      left: isArabic ? "8px" : "unset",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "12px",
                      color: "#fff",
                    }}
                  >
                    {isArabic ? "◀" : "▶"}
                  </div>
                )}
              </div>
            ))}
            <Box
              onClick={() => {
                setCustomIndexDialogOpen(true);
                handleCloseMenu();
              }}
              sx={{
                padding: "12px 20px",
                color: "#339ace",
                cursor: "pointer",
                textAlign: isArabic ? "right" : "left",
                fontWeight: "700",
                borderRadius: "0px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                borderTop: "1px solid rgba(51, 154, 206, 0.2)",
                backgroundColor: "rgba(51, 154, 206, 0.05)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                "&:hover": {
                  backgroundColor: "rgba(51, 154, 206, 0.12)",
                  color: "#2b8bb8",
                }
              }}
            >
              <SearchIcon sx={{ fontSize: "18px" }} />
              {t("CustomIndex.button")}
            </Box>
          </div>

          {/* Enhanced Second column: types */}
          {hoveredCategory && (
            <div 
              style={{ 
                width: 'max-content', 
                minWidth: "150px",
                borderLeft: "1px solid rgba(51, 154, 206, 0.1)",
                // backgroundColor: "rgba(255, 255, 255, 0.9)"
              }}
            >
              {Object.keys(groupedCategories[hoveredCategory]).map((type, index) => (
                <div
                  key={type}
                  onMouseEnter={() => {
                    setHoveredType(type);
                    setHoveredOptionIdx(null);
                  }}
                  style={{
                    padding: "12px 20px",
                    background:
                      selectedItems.currentType === type &&
                      selectedItems.currentCategory === hoveredCategory
                        ? "linear-gradient(90deg, #339ace 0%, #2b8bb8 100%)"
                        : hoveredType === type
                        ? "linear-gradient(90deg,rgb(116, 196, 236) 0%,rgb(47, 160, 212) 100%)"
                        : undefined,
                    color:
                      selectedItems.currentType === type &&
                      selectedItems.currentCategory === hoveredCategory
                        ? "#fff"
                        : hoveredType === type
                        ? "#fff"
                        : "#444",
                    cursor: "pointer",
                    textAlign: isArabic ? "right" : "left",
                    fontWeight: selectedItems.currentType === type && selectedItems.currentCategory === hoveredCategory ? "800" : "700",
                    borderRadius: "0px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderBottom: index === Object.keys(groupedCategories[hoveredCategory]).length - 1 ? "none" : "1px solid rgba(0,0,0,0.05)",
                    position: "relative",
                  }}
                >
                  {groupedCategoriesLabels[hoveredCategory]?.["types"]?.[type]?.["name"]}
                  {hoveredType === type && (
                    <div
                      style={{
                        position: "absolute",
                        right: isArabic ? "unset" : "8px",
                        left: isArabic ? "8px" : "unset",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "12px",
                        color: "#fff",
                      }}
                    >
                      {isArabic ? "◀" : "▶"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Enhanced Third column: options */}
          {hoveredCategory && hoveredType && (
            <div 
              style={{ 
                width: 'max-content', 
                minWidth: "220px",
                borderLeft: "1px solid rgba(51, 154, 206, 0.1)",
                // backgroundColor: "rgba(255, 255, 255, 0.9)"
              }}
            >
              {groupedCategories[hoveredCategory][hoveredType].map((option, idx) => (
                <div
                  key={option.id}
                  onClick={(event) => {
                    handleMenuItemClick(
                      event,
                      hoveredCategory,
                      hoveredType,
                      option.label,
                      option.id
                    );
                    handleCloseMenu();
                  }}
                  onMouseEnter={() => setHoveredOptionIdx(idx)}
                  onMouseLeave={() => setHoveredOptionIdx(null)}
                  style={{
                    padding: "12px 20px",
                    background:
                      selectedItems[hoveredCategory] === option.label
                        ? "linear-gradient(90deg, #339ace 0%, #2b8bb8 100%)"
                        : hoveredOptionIdx === idx
                        ? "linear-gradient(90deg,rgb(116, 196, 236) 0%,rgb(47, 160, 212) 100%)"
                        : undefined,
                    color:
                      selectedItems[hoveredCategory] === option.label
                        ? "#fff"
                        : hoveredOptionIdx === idx
                        ? "#fff"
                        : "#444",
                    cursor: "pointer",
                    textAlign: isArabic ? "right" : "left",
                    fontWeight: selectedItems[hoveredCategory] === option.label ? "800" : "700",
                    borderRadius: "0px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderBottom: idx === groupedCategories[hoveredCategory][hoveredType].length - 1 ? "none" : "1px solid rgba(0,0,0,0.05)",
                    position: "relative",
                  }}
                >
                  {
                    groupedCategoriesLabels[hoveredCategory]?.["types"]?.[hoveredType]?.[
                      "options"
                    ]?.[idx]?.[isArabic ? "label_ar" : "label"]
                  }
                  {selectedItems[hoveredCategory] === option.label && (
                    <div
                      style={{
                        position: "absolute",
                        right: isArabic ? "unset" : "12px",
                        left: isArabic ? "12px" : "unset",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "14px",
                        color: "#fff",
                        opacity: 0.8,
                      }}
                    >
                      ✓
                    </div>
                  )}
                </div>
              ))}
              <Box
                onClick={() => {
                  setCustomIndexDialogOpen(groupedCategoriesLabels[hoveredCategory]['types'][hoveredType]['name']);
                  handleCloseMenu();
                }}
                sx={{
                  padding: "12px 20px",
                  color: "#339ace",
                  cursor: "pointer",
                  textAlign: isArabic ? "right" : "left",
                  fontWeight: "700",
                  borderRadius: "0px",
                  borderTop: "1px solid rgba(51, 154, 206, 0.1)",
                  backgroundColor: "rgba(51, 154, 206, 0.05)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: "rgba(51, 154, 206, 0.12)",
                    color: "#2b8bb8",
                  }
                }}
              >
                {t("CustomIndex.type_custom")}
              </Box>
            </div>
          )}
        </div>, document.body
      )}
    </div>
  );
}